// Game mode presentation (docs/GAME-MODES-ROADMAP.md §1 D23–D25, GDD §3): the name, tagline and rules text the
// setup screen shows for each mode. Every number in the rules is read from content (GAME_MODES, CAPTURE,
// TRANSPORT_ROUTING, the city dataset), so the text cannot drift from the war the sim runs. Map rules come from a
// registry keyed by the map rules' kind, each entry reading its own typed rules; the claim rule, the transport rule, the
// mode's troop cap, territory, approval and expansion rules (a line only where they differ from a homeland city, the
// duel's public and the nearest free city), the clock and the win conditions are shared.

import {
  ALL_CITIES,
  CAPTURE,
  GAME_MODES,
  PLAYABLE_COUNTRIES,
  TRANSPORT_ROUTING,
  type BodyCap,
  type GameModeDef,
  type GameModeKey,
  type MatchClockDef,
  type WarMapRules,
} from '@taxwar/content'
import { HudError } from '../../hud/errors'
import { formatClock, formatWhole } from '../../hud/format'

export interface ModePresentation {
  readonly name: string
  readonly tagline: string
  /** Shown under the COMING SOON stamp while the mode is a preview this build does not offer (app/release.ts). */
  readonly teaser: string
}

export const MODE_PRESENTATION: Readonly<Record<GameModeKey, ModePresentation>> = {
  duel: {
    name: 'Country vs Country Duel',
    tagline: 'Two nations, one border dispute, a fixed-price war.',
    teaser: 'Clearance pending: the border dispute is still in committee.',
  },
  world_war: {
    name: 'World War',
    tagline: 'Every city on Earth is on the map. So is the invoice.',
    teaser: `Clearance pending: accounting is still itemising ${formatWhole(ALL_CITIES.length)} cities.`,
  },
}

/** "6–8", or "6" when the range is one value. */
function formatRange(min: number, max: number): string {
  return min === max ? formatWhole(min) : `${formatWhole(min)}–${formatWhole(max)}`
}

function formatSeconds(seconds: number): string {
  return `${formatWhole(seconds)} s`
}

const PERCENT = 100

function formatPercent(fraction: number): string {
  return `${formatWhole(fraction * PERCENT)}%`
}

interface CityCountRange {
  readonly fewest: number
  readonly most: number
}

/** How many cities a side brings: every playable country fields all of its cities, in both modes. */
function countryCityRange(): CityCountRange {
  if (PLAYABLE_COUNTRIES.length === 0) throw new HudError('the content dataset has no playable country')
  let fewest = Infinity
  let most = 0
  for (const country of PLAYABLE_COUNTRIES) {
    fewest = Math.min(fewest, country.cities.length)
    most = Math.max(most, country.cities.length)
  }
  return { fewest, most }
}

const COUNTRY_CITIES = countryCityRange()

type MapRulesByKind = { readonly [R in WarMapRules as R['kind']]: R }
type MapRulesKind = keyof MapRulesByKind

const MAP_RULE_LINES: { readonly [K in MapRulesKind]: (rules: MapRulesByKind[K]) => readonly string[] } = {
  duel: (rules) => [
    `Each side fields every city of its country: ${formatRange(COUNTRY_CITIES.fewest, COUNTRY_CITIES.most)}, as listed by each nation. Fewer cities, harder war.`,
    `The ${formatRange(rules.neutralCapitalsMin, rules.neutralCapitalsMax)} neutral capitals nearest the midpoint between the two capitals are free territory, and so is every town the roads between them pass through.`,
    'A side with no ocean port of its own also gets the nearest one on the map, with the towns on the road there: take it and build a Port.',
    `The map is scaled so the capitals are ${formatWhole(rules.capitalDistanceMu)} mu apart. Every duel plays at the same pace, whatever the atlas says.`,
  ],
  world: () => [
    `All ${formatWhole(ALL_CITIES.length)} cities of the world are on the map.`,
    `Each side starts with every city of its country (up to ${formatWhole(COUNTRY_CITIES.most)}); every other city is neutral territory to claim.`,
    'One fixed scale for the whole globe: neighbours fight a short war, distant enemies mean long marches and sea crossings.',
  ],
}

function mapRuleLines<K extends MapRulesKind>(kind: K, rules: MapRulesByKind[K]): readonly string[] {
  return MAP_RULE_LINES[kind](rules)
}

const CLAIM_LINE = `Neutral territory is unarmed. A land unit claims a city by holding it ${formatSeconds(CAPTURE.uncontestedSeconds)} with no enemy there.`

/** docs/TRANSPORT.md as the ground planner applies it: the airlift rule's margin is the ratio above an even trade. */
const TRANSPORT_LINE = `Land armies never cross water on foot. A Port's boats ferry them from any coast of its water; an Airbase's planes fly them anywhere walking and ferries would take at least ${formatPercent(TRANSPORT_ROUTING.airliftMinGroundRatio - 1)} longer to reach, or cannot reach at all.`

/** A homeland city's weight, the duel's public and the nearest free city: the values that need no rule line. */
const HOMELAND_WEIGHT = 1
/** Free territory that pays nothing at all. */
const UNTAXED_WEIGHT = 0
const FULL_APPROVAL_EVENTS = 1
const NO_EXPANSION_PULL = 0

function territoryLines(weight: number): readonly string[] {
  if (weight === HOMELAND_WEIGHT) return []
  if (weight === UNTAXED_WEIGHT) return ['Claimed territory pays no taxes, and nobody at home cheers its claim or mourns its loss. Enemy cities you capture still pay.']
  return [`Claimed territory pays ${formatPercent(weight)} of a city's taxes and moves approval ${formatPercent(weight)} as much. Enemy cities you capture pay in full.`]
}

function approvalLines(scale: number): readonly string[] {
  if (scale === FULL_APPROVAL_EVENTS) return []
  return [`A long war numbs the public: captures, losses, strikes and casualties move approval ${formatPercent(scale)} as much.`]
}

function expansionLines(pull: number): readonly string[] {
  return pull === NO_EXPANSION_PULL ? [] : ['Armies claim the free territory on their way to the enemy, not every city around them.']
}

function bodyCapLine(cap: BodyCap): string {
  return cap.kind === 'capped' ? `Up to ${formatWhole(cap.bodiesPerPlayer)} troops per side.` : 'No troop limit: money alone paces each army.'
}

/** The mode's rules beyond its map: troop cap, territory, approval and expansion. */
function modeRuleLines(def: GameModeDef): readonly string[] {
  return [
    bodyCapLine(def.bodyCap),
    ...territoryLines(def.territoryWeight),
    ...approvalLines(def.approvalEventScale),
    ...expansionLines(def.expansionEnemyPull),
  ]
}

/** GDD §10 as the sim's OutcomeSystem decides it. */
const WIN_LINES: readonly string[] = [
  'Destroy the enemy capital to win. Lose your own capital, or all of your approval, and you lose.',
  'When regulation ends, the side holding more cities wins. A tie goes to overtime: the first capture that breaks it wins; if none does, higher approval wins.',
]

function clockLine(clock: MatchClockDef): string {
  return `${formatClock(clock.regulationSeconds)} regulation + ${formatClock(clock.overtimeSeconds)} overtime.`
}

/** The rules of a mode as the setup screen lists them: its map, the claim and transport rules, its own rules, its clock, the win conditions. */
export function modeRules(mode: GameModeKey): readonly string[] {
  const def = GAME_MODES[mode]
  return [...mapRuleLines(def.map.kind, def.map), CLAIM_LINE, TRANSPORT_LINE, ...modeRuleLines(def), clockLine(def.clock), ...WIN_LINES]
}
